import styled from '@emotion/styled'
import tokens from '@lacedltd/design-tokens'
import { mediaQuery } from '@lacedltd/laced-components'

/* ── Palette — Minimalist Luxury ── */
const c = {
  bg: '#F9F9F8',
  surface: '#F4F3F1',
  surfaceHover: '#EEEDEB',
  border: '#E8E7E5',
  borderLight: '#F0EFED',
  text: '#1A1A1A',
  textSecondary: '#6B6B6B',
  textMuted: '#9B9B9B',
  selected: '#1A1A1A',
  btnPrimary: '#1A1A1A',
  btnPrimaryText: '#FFFFFF',
  btnPrimaryHover: '#2D2D2D',
  btnSecondary: 'transparent',
  btnSecondaryText: '#6B6B6B',
  btnSecondaryHover: '#1A1A1A',
  btnDisabledBg: '#F0EFED',
  btnDisabledText: '#C5C5C3',
  error: '#9B1C1C',
  errorBg: '#FDF2F2',
  errorBorder: '#F5D0D0',
  white: '#FFFFFF',
  shadow: '0 4px 24px rgba(0, 0, 0, 0.06)',
}

/* ── Modal shell ── */

export const ModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: min(480px, 92vw);
  max-height: 85vh;
  background: ${c.bg};
  border-radius: 12px;
  overflow: hidden;

  ${mediaQuery.m.max} {
    /* Cancel the parent ModalContent's 2rem padding so we fill edge-to-edge */
    width: calc(100% + ${tokens.spacing[7]} * 2);
    max-width: none;
    margin: -${tokens.spacing[7]};
    margin-bottom: 0;
    max-height: 92vh;
    border-radius: ${tokens.spacing[3]} ${tokens.spacing[3]} 0 0;

    &::before {
      content: '';
      display: block;
      width: 36px;
      height: 4px;
      background: ${c.border};
      border-radius: 2px;
      margin: 10px auto 2px;
      flex-shrink: 0;
    }
  }
`

export const ModalHeader = styled.div`
  padding: 24px 56px 20px 24px;

  ${mediaQuery.m.max} {
    padding: 16px 48px 14px 20px;
  }
`

export const Title = styled.h2`
  margin: 0;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${c.textSecondary};
`

export const Steps = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  margin-top: 14px;
`

export const StepLabel = styled.span<{ active: boolean; done: boolean }>`
  font-size: 13px;
  letter-spacing: 0.01em;
  color: ${({ active, done }) =>
    active ? c.text : done ? c.textSecondary : c.textMuted};
  font-weight: ${({ active }) => (active ? '500' : '400')};
  transition: color 0.2s ease;

  &:not(:last-child)::after {
    content: '';
    display: inline-block;
    width: 20px;
    height: 1px;
    background: ${c.border};
    vertical-align: middle;
    margin: 0 10px;
  }
`

/* ── Body ── */

export const Body = styled.div`
  padding: 24px;
  overflow-y: auto;
  flex: 1;
  -webkit-overflow-scrolling: touch;

  ${mediaQuery.m.max} {
    padding: 20px;
  }
`

/* ── Footer ── */

export const Footer = styled.div`
  padding: 16px 24px 20px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;

  ${mediaQuery.m.max} {
    padding: 16px 20px calc(env(safe-area-inset-bottom, 0px) + 20px);
    flex-direction: column-reverse;
    gap: 10px;
  }
`

/* ── Buttons ── */

export const PrimaryBtn = styled.button<{ disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  padding: 0 24px;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.02em;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.2s ease, opacity 0.2s ease;
  background: ${({ disabled }) => (disabled ? c.btnDisabledBg : c.btnPrimary)};
  color: ${({ disabled }) => (disabled ? c.btnDisabledText : c.btnPrimaryText)};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};

  &:hover {
    background: ${c.btnPrimaryHover};
  }

  &:active {
    opacity: 0.85;
  }

  ${mediaQuery.m.max} {
    width: 100%;
    height: 48px;
    font-size: 14px;
  }
`

export const SecondaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  padding: 0 16px;
  font-size: 13px;
  font-weight: 400;
  letter-spacing: 0.01em;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  background: ${c.btnSecondary};
  color: ${c.btnSecondaryText};
  transition: color 0.2s ease;

  &:hover {
    color: ${c.btnSecondaryHover};
  }

  &:active {
    opacity: 0.7;
  }

  ${mediaQuery.m.max} {
    width: 100%;
    height: 48px;
    font-size: 14px;
  }
`

export const LoadingDots = styled.span`
  &::after {
    content: '...';
    animation: ellipsis 1.2s steps(4, end) infinite;
  }

  @keyframes ellipsis {
    0% { content: ''; }
    25% { content: '.'; }
    50% { content: '..'; }
    75% { content: '...'; }
  }
`

/* ── Product context card ── */

export const ProductCard = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px;
  background: ${c.surface};
  border-radius: 8px;
  margin-bottom: 24px;
`

export const ProductImage = styled.img`
  width: 52px;
  height: 52px;
  object-fit: contain;
  border-radius: 4px;
  background: ${c.white};
  flex-shrink: 0;
`

export const ProductInfo = styled.div`
  flex: 1;
  min-width: 0;
`

export const ProductTitle = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${c.text};
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const ProductMeta = styled.div`
  font-size: 12px;
  color: ${c.textSecondary};
  margin-top: 3px;
  letter-spacing: 0.02em;
`

/* ── Provider step ── */

export const ProviderList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

export const ProviderCard = styled.label<{ selected: boolean }>`
  /* !important needed: global CSS sets label { display: block } */
  display: flex !important;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  min-height: 52px;
  border: 1px solid ${({ selected }) => (selected ? c.selected : c.border)};
  border-radius: 8px;
  cursor: pointer;
  background: ${({ selected }) => (selected ? c.surface : c.white)};
  transition: all 0.2s ease;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    background: ${({ selected }) => (selected ? c.surface : c.surfaceHover)};
    border-color: ${({ selected }) => (selected ? c.selected : '#D4D3D1')};
  }

  &:active {
    transform: scale(0.995);
  }
`

export const ProviderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

/* Native radio is globally hidden — keep it for a11y but visually hidden */
export const ProviderRadio = styled.input`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  pointer-events: none;
`

/* Visual radio indicator rendered as a sibling span */
export const RadioDot = styled.span<{ checked: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border: 1.5px solid ${({ checked }) => (checked ? c.selected : c.border)};
  border-radius: 50%;
  flex-shrink: 0;
  transition: border-color 0.2s ease;

  &::after {
    content: '';
    display: block;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${c.selected};
    transform: scale(${({ checked }) => (checked ? 1 : 0)});
    transition: transform 0.15s ease;
  }
`

export const ProviderName = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${c.text};
  letter-spacing: 0.01em;
`

export const ProviderFee = styled.span`
  font-size: 13px;
  font-weight: 400;
  color: ${c.textSecondary};
  letter-spacing: 0.02em;
  white-space: nowrap;
`

/* ── Upload step ── */

export const DropZone = styled.label<{
  isDragging: boolean
  hasError: boolean
}>`
  /* !important needed: global CSS sets label { display: block } */
  display: flex !important;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  border: 1.5px dashed
    ${({ isDragging, hasError }) =>
      hasError ? c.error : isDragging ? c.selected : c.border};
  border-radius: 8px;
  cursor: pointer;
  text-align: center;
  background: ${({ isDragging }) => (isDragging ? c.surface : c.white)};
  transition: border-color 0.2s ease, background 0.2s ease;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    border-color: ${c.textMuted};
    background: ${c.surface};
  }

  ${mediaQuery.m.max} {
    padding: 36px 16px;
    min-height: 160px;
  }
`

export const DropZoneIcon = styled.div`
  width: 40px;
  height: 40px;
  margin-bottom: 12px;
  color: ${c.textMuted};
  display: flex;
  align-items: center;
  justify-content: center;
`

export const DropZoneText = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${c.textSecondary};
  margin-top: 4px;
`

export const DropZoneHint = styled.div`
  font-size: 12px;
  color: ${c.textMuted};
  margin-top: 6px;
  letter-spacing: 0.01em;
`

export const HiddenInput = styled.input`
  display: none;
`

export const FileRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  background: ${c.surface};
  border-radius: 8px;
  margin-top: 12px;
`

export const FileName = styled.div`
  font-size: 13px;
  color: ${c.text};
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  svg {
    flex-shrink: 0;
    color: ${c.textMuted};
  }
`

export const ReplaceLink = styled.button`
  background: none;
  border: none;
  color: ${c.textSecondary};
  font-size: 12px;
  font-weight: 400;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
  padding: 8px 4px;
  flex-shrink: 0;
  min-height: 44px;
  display: flex;
  align-items: center;
  transition: color 0.15s ease;

  &:hover {
    color: ${c.text};
  }
`

export const ErrorText = styled.div`
  margin: 10px 0 0;
  font-size: 13px;
  color: ${c.error};
  letter-spacing: 0.01em;
`

/* ── Confirm step ── */

export const SummaryCard = styled.div`
  background: ${c.white};
  border-radius: 8px;
  box-shadow: 0 0 0 1px ${c.borderLight};
  overflow: hidden;
`

export const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  font-size: 13px;

  &:not(:last-child) {
    border-bottom: 1px solid ${c.borderLight};
  }
`

export const SummaryLabel = styled.span`
  color: ${c.textSecondary};
  letter-spacing: 0.01em;
`

export const SummaryValue = styled.span`
  color: ${c.text};
  font-weight: 500;
  text-align: right;
  max-width: 60%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const FeeHighlight = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: ${c.text};
  letter-spacing: -0.01em;
`

export const Caption = styled.div`
  margin-top: 16px;
  font-size: 12px;
  color: ${c.textMuted};
  text-align: center;
  line-height: 1.5;
  letter-spacing: 0.01em;
`

export const BalanceWarning = styled.div`
  margin-top: 14px;
  padding: 12px 14px;
  background: ${c.errorBg};
  border: 1px solid ${c.errorBorder};
  border-radius: 8px;
  font-size: 13px;
  color: ${c.error};
  line-height: 1.5;
`

/* ── Inline retrieval action buttons (on seller item cards) ── */

export const RetrievalActions = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;

  ${mediaQuery.m.max} {
    flex-direction: column;
    width: 100%;
    gap: 4px;
  }
`

export const RetrievalActionBtn = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 30px;
  padding: 0 12px;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.01em;
  white-space: nowrap;
  border: 1px solid ${tokens.color.airJordan[300]};
  border-radius: ${tokens.borderRadius.s};
  background: transparent;
  color: ${tokens.color.airJordan[500]};
  cursor: pointer;
  transition: all 0.15s ease;
  text-decoration: none;
  line-height: 1;

  &:hover {
    border-color: ${tokens.color.airJordan[500]};
    color: ${tokens.color.airJordan.p};
    background: ${tokens.color.airJordan[50]};
  }

  ${mediaQuery.m.max} {
    width: 100%;
    height: 34px;
    font-size: 13px;
    background: ${tokens.color.airJordan[50]};
    border-color: ${tokens.color.airJordan[200]};
  }
`

export const RetrievalCancelBtn = styled.a<{ disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 400;
  white-space: nowrap;
  border: none;
  background: transparent;
  color: ${c.error};
  cursor: pointer;
  padding: 2px 4px;
  transition: opacity 0.15s ease;
  text-decoration: none;
  line-height: 1;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 0.7)};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};

  &:hover {
    opacity: 1;
  }

  ${mediaQuery.m.max} {
    font-size: 12px;
    padding: 4px;
  }
`
